import React, { useEffect, useRef, useState } from 'react';
import useVendorAccess from './useVendorAccess';
import VendorList from './VendorList';
import VendorForm from './VendorForm';
import ReviewQueue from './ReviewQueue';
import UntaggedQueue from './UntaggedQueue';
import MyVendorRequests from './MyVendorRequests';
import RequestAccessModal from './RequestAccessModal';
import { Skeleton, FullError } from './VendorBits';
import './Vendors.css';

const VALID = new Set(['list', 'review', 'untagged', 'mine']);

function readTabFromHash() {
  if (typeof window === 'undefined') return 'list';
  const m = window.location.hash.match(/tab=([a-z]+)/i);
  return m && VALID.has(m[1]) ? m[1] : 'list';
}

function writeTabToHash(tab) {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  params.set('tab', tab);
  const hash = `#${params.toString()}`;
  if (window.location.hash !== hash) {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
  }
}

/** /vendors: the register, the owner queue, triage and the caller's own requests. */
export default function VendorsPage() {
  const { loading, error, me, isOwner, refresh } = useVendorAccess();
  const [active, setActive] = useState(readTabFromHash);
  const [requestOpen, setRequestOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [listKey, setListKey] = useState(0);
  const tabRefs = useRef({});

  const showReview = !!me && isOwner;
  const showUntagged = !!me && (!!me.triage || !!me.is_admin);

  const tabs = [
    { key: 'list', label: 'All vendors' },
    // Red means "waiting on you"; the untagged backlog is work, not an alarm.
    ...(showReview ? [{ key: 'review', label: 'To review', count: me?.counts?.review }] : []),
    ...(showUntagged ? [{ key: 'untagged', label: 'Untagged', count: me?.counts?.untagged, muted: true }] : []),
    { key: 'mine', label: 'My requests' },
  ];

  useEffect(() => { writeTabToHash(active); }, [active]);
  // Email and sidebar links land on #tab=… while the page may already be open;
  // without this, the URL changes but the visible tab does not.
  useEffect(() => {
    const onHash = () => setActive(readTabFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  // A tab the caller can't see (deep link) falls back to the list.
  useEffect(() => {
    if (me && !tabs.some((t) => t.key === active)) setActive('list');
    // tabs is derived from me; keyed on those inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, showReview, showUntagged, active]);

  const onTabKeyDown = (e) => {
    const i = tabs.findIndex((t) => t.key === active);
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    const key = tabs[next].key;
    setActive(key);
    tabRefs.current[key]?.focus();
  };


  let body;
  if (loading && !me) {
    body = <Skeleton label="Loading vendors" />;
  } else if (error || !me) {
    body = <FullError message="Couldn't load vendors. Try again in a minute." onRetry={refresh} />;
  } else {
    body = (
      <>
        <div className="admin-subtabs admin-subtabs--scroll" role="tablist" aria-label="Vendor views">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`vd-tab-${t.key}`}
              ref={(el) => { tabRefs.current[t.key] = el; }}
              aria-selected={active === t.key}
              aria-controls="vd-tabpanel"
              tabIndex={active === t.key ? 0 : -1}
              className={`admin-subtab ${active === t.key ? 'is-active' : ''}`}
              onClick={() => setActive(t.key)}
              onKeyDown={onTabKeyDown}
            >
              {t.label}
              {t.count > 0 && (
                <span className={`admin-subtab-badge ${t.muted ? 'admin-subtab-badge--muted' : ''}`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
        <div className={`vd-panel${active === 'list' ? ' vl-panel--bare' : ''}`} role="tabpanel" id="vd-tabpanel" aria-labelledby={`vd-tab-${active}`}>
          {active === 'list' && (
            <VendorList key={listKey} me={me} onRequestAccess={() => setRequestOpen(true)} onAddVendor={() => setAdding(true)} />
          )}
          {active === 'review' && showReview && <ReviewQueue me={me} onChanged={refresh} />}
          {active === 'untagged' && showUntagged && <UntaggedQueue onChanged={refresh} />}
          {active === 'mine' && <MyVendorRequests />}
        </div>
        {requestOpen && (
          <RequestAccessModal
            me={me}
            onClose={() => setRequestOpen(false)}
            onDone={() => { setRequestOpen(false); setActive('mine'); }}
          />
        )}
        {adding && (
          <VendorForm
            open
            mode="create"
            vendor={null}
            me={me}
            isoFieldsKnown
            onClose={() => setAdding(false)}
            onSubmitted={() => { setAdding(false); setListKey((k) => k + 1); setActive('mine'); }}
          />
        )}
      </>
    );
  }

  return (
    <div className="main-content-inner vd-page">
      <div className="page-head">
        <p className="page-eyebrow">Suppliers</p>
        <div className="page-title-row">
          <h1 className="page-title">Vendors</h1>
          <p className="page-subtitle">Suppliers you work with, by speciality. Changes are approved by the speciality owner before they reach Odoo.</p>
        </div>
      </div>
      {body}
    </div>
  );
}
